import { AnimalAvatar } from "@/components/animal-avatar";
import {
  MagnifyingGlassIcon,
  QuestionMarkCircledIcon,
  StarFilledIcon,
  StarIcon,
} from "@radix-ui/react-icons";
import {
  Button,
  Dialog,
  Flex,
  IconButton,
  Table,
  TextField,
} from "@radix-ui/themes";
import { forwardRef, useState } from "react";
import toast from "react-hot-toast";
import { useCopyToClipboard } from "react-use";
import { Id } from "vis-data/declarations/data-interface";

type FilterSectionProps = {
  showSavedOnly: boolean;
  hasShowSavedButton: boolean;
  onFilterToggle: () => void;
  getAllTracks?: () => Id[];
};

const FilterSection = forwardRef<HTMLDivElement, FilterSectionProps>(
  (
    { showSavedOnly, onFilterToggle, hasShowSavedButton, getAllTracks },
    ref
  ) => {
    const renderShowSavedButton = () => {
      if (!hasShowSavedButton) {
        return null;
      }

      return (
        <IconButton
          variant="ghost"
          color="gray"
          size="1"
          mr="1"
          onClick={onFilterToggle}
        >
          {showSavedOnly ? (
            <StarFilledIcon width={16} height={16} />
          ) : (
            <StarIcon width={16} height={16} />
          )}
        </IconButton>
      );
    };
    return (
      <Flex
        ref={ref}
        className="h-12 w-64"
        position="absolute"
        align="end"
        justify="between"
        p="2"
      >
        <TextField.Root placeholder="Search" variant="soft" size="1" disabled>
          <TextField.Slot>
            <MagnifyingGlassIcon height="16" width="16" />
          </TextField.Slot>
        </TextField.Root>
        <Dialog.Root>
          <Dialog.Trigger>
            <IconButton size="1" variant="ghost" color="gray">
              <QuestionMarkCircledIcon />
            </IconButton>
          </Dialog.Trigger>
          <Dialog.Content className="p-0 pt-1">
            <TrackToAvatarMapping getAllTracks={getAllTracks} />
          </Dialog.Content>
        </Dialog.Root>
        {renderShowSavedButton()}
      </Flex>
    );
  }
);

export default FilterSection;

function TrackToAvatarMapping({ getAllTracks }: { getAllTracks?: () => Id[] }) {
  const [trackIds] = useState(() => getAllTracks?.() || []);

  const [_, copy] = useCopyToClipboard();

  const handleCopy = (text: string) => {
    copy(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Table.Root
      size="1"
      className="[&_[data-radix-scroll-area-viewport]]:max-h-[600px]"
    >
      <Table.Header className="sticky top-0 bg-[var(--gray-2)]">
        <Table.Row>
          <Table.ColumnHeaderCell className="pl-5">
            Avatar
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Track ID</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body className="p-5">
        {trackIds.map((trackId) => (
          <Table.Row key={trackId}>
            <Table.Cell width="100px" className="pl-5">
              <AnimalAvatar
                size="64px"
                showTitle
                trackId={String(trackId)}
                wrappClassName="justify-start"
              />
            </Table.Cell>
            <Table.Cell className="align-middle">
              <Button
                variant="ghost"
                size="2"
                color="gray"
                highContrast
                onClick={() => handleCopy(String(trackId))}
              >
                {trackId}
              </Button>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
